// SuperAdmin.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Super Admin Panel — Binko
// Self-contained. No external deps beyond MUI (already in project).
// Auth: uses the caller's existing binko_token (normal user JWT).
//       The phone on that JWT must be in ADMIN_PHONES env var on the backend.
// Route: /superadmin — intercepted in App.js before user auth logic.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Button, TextField, Chip, Switch,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, InputLabel, FormControl, Tabs, Tab,
  Alert, Snackbar, Divider, Paper,
} from '@mui/material';

// ─── Config ──────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'https://api.binko.me';

function authHeaders() {
  const token = localStorage.getItem('binko_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// ─── Colour palette (matches Binko dark theme) ───────────────────────────────
const C = {
  bg:       '#0e0e14',
  surface:  '#16161f',
  surface2: '#1e1e2c',
  border:   'rgba(255,255,255,0.07)',
  teal:     '#00bfa5',
  pink:     '#ff4d8f',
  amber:    '#ffb830',
  purple:   '#9b5de5',
  red:      '#ff4560',
  green:    '#00e5a0',
  text:     '#f0f0f5',
  muted:    '#7a7a8c',
  dim:      '#4a4a5a',
};

// ─── Status helpers ───────────────────────────────────────────────────────────
function statusColor(s) {
  if (s === 'ACTIVE')     return C.green;
  if (s === 'BLOCKED')    return C.red;
  if (s === 'WAITLISTED') return C.amber;
  return C.muted;
}
function statusChip(label, color) {
  return (
    <Chip label={label} size="small" sx={{
      background: `${color}22`, color, fontWeight: 700,
      fontSize: '0.68rem', height: 20, borderRadius: '4px',
    }} />
  );
}
function subStatusChip(validUntil) {
  if (!validUntil) return statusChip('No sub', C.muted);
  const days = Math.ceil((new Date(validUntil) - new Date()) / 86400000);
  if (days < 0)  return statusChip('Expired', C.red);
  if (days <= 5) return statusChip(`${days}d left`, C.amber);
  if (days <= 30) return statusChip(`${days}d left`, C.amber);
  return statusChip(`${days}d left`, C.green);
}

// ─── Shared table styles ─────────────────────────────────────────────────────
const TH = { color: C.muted, fontWeight: 700, fontSize: '0.72rem',
             textTransform: 'uppercase', letterSpacing: '0.5px',
             borderBottom: `1px solid ${C.border}`, py: 1, px: 1.5 };
const TD = { color: C.text, fontSize: '0.82rem',
             borderBottom: `1px solid ${C.border}`, py: 1, px: 1.5 };

// ─── Stat Card ───────────────────────────────────────────────────────────────
function StatCard({ label, value, color = C.teal }) {
  return (
    <Paper sx={{
      flex: 1, minWidth: 140, background: C.surface2,
      border: `1px solid ${C.border}`, borderRadius: 2, p: 2.5,
    }}>
      <Typography sx={{ color: C.muted, fontSize: '0.75rem', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ color, fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>{value ?? '—'}</Typography>
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: USERS
// ─────────────────────────────────────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('ALL');
  const [selected, setSelected] = useState(null);  // user detail
  const [actionUser, setActionUser] = useState(null);
  const [action, setAction]     = useState('');
  const [days, setDays]         = useState(7);
  const [working, setWorking]   = useState(false);
  const [toast, setToast]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filter === 'NEEDS_REVIEW') params.set('needs_review', '1');
      const res = await fetch(`${API}/api/admin/users?${params}`, { headers: authHeaders() });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (_) {}
    setLoading(false);
  }, [search, filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'ALL' || filter === 'NEEDS_REVIEW'
    ? users
    : users.filter(u => u.effectiveStatus === filter);

  async function loadDetail(userId) {
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}`, { headers: authHeaders() });
      const data = await res.json();
      setSelected(data);
    } catch (_) {}
  }

  async function doAction() {
    if (!actionUser || !action) return;
    setWorking(true);
    try {
      const res = await fetch(`${API}/api/admin/users/${actionUser.id}/action`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ action, days: action === 'suspend' ? days : undefined }),
      });
      const data = await res.json();
      if (data.ok) {
        setToast(`Done: ${action} applied to @${actionUser.alias}`);
        setActionUser(null);
        setAction('');
        if (selected?.user?.id === actionUser.id) {
          await loadDetail(actionUser.id);
        }
        load();
      }
    } catch (_) {}
    setWorking(false);
  }

  const ACTION_OPTIONS = [
    { value: 'force_active',   label: '✅ Force Active (approve off waitlist)' },
    { value: 'warn',           label: '⚠️  Issue Warning (+1 warning count)' },
    { value: 'suspend',        label: '⏸️  Suspend (time-limited)' },
    { value: 'ban',            label: '🚫 Permanent Ban' },
    { value: 'force_waitlist', label: '↩️  Move to Waitlist' },
    { value: 'clear_override', label: '🔄 Clear Override (back to DB status)' },
    { value: 'clear_review',   label: '✔️  Mark as Reviewed' },
  ];

  const FILTERS = ['ALL', 'ACTIVE', 'WAITLISTED', 'BLOCKED', 'NEEDS_REVIEW'];

  return (
    <Box>
      {/* Search + filter bar */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search alias or phone…" value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { color: C.text, borderColor: C.border },
                '& .MuiInputBase-input': { color: C.text },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }}
          inputProps={{ style: { color: C.text } }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <Chip key={f} label={f} clickable onClick={() => setFilter(f)}
              sx={{
                background: filter === f ? `${C.teal}22` : C.surface2,
                color: filter === f ? C.teal : C.muted,
                border: `1px solid ${filter === f ? C.teal : C.border}`,
                fontWeight: 600, fontSize: '0.72rem',
              }} />
          ))}
        </Box>
      </Box>

      {loading ? <CircularProgress size={24} sx={{ color: C.teal }} /> : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Alias','Phone','Gender','Status','Warnings','Joined','Actions'].map(h => (
                  <TableCell key={h} sx={TH}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(u => (
                <TableRow key={u.id}
                  sx={{ background: u.needs_review ? `${C.amber}10` : 'transparent',
                        '&:hover': { background: C.surface2 }, cursor: 'pointer' }}
                  onClick={() => loadDetail(u.id)}
                >
                  <TableCell sx={TD}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      {u.needs_review ? <span title="Needs review">🔴</span> : null}
                      @{u.alias || '—'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...TD, color: C.muted }}>{u.phone}</TableCell>
                  <TableCell sx={TD}>{u.gender || '—'}</TableCell>
                  <TableCell sx={TD}>{statusChip(u.effectiveStatus, statusColor(u.effectiveStatus))}</TableCell>
                  <TableCell sx={{ ...TD, color: u.warning_count > 0 ? C.amber : C.muted }}>
                    {u.warning_count || 0}
                  </TableCell>
                  <TableCell sx={{ ...TD, color: C.muted }}>
                    {new Date(u.created_at).toLocaleDateString('en-IN')}
                  </TableCell>
                  <TableCell sx={TD} onClick={e => e.stopPropagation()}>
                    <Button size="small" variant="outlined"
                      onClick={() => { setActionUser(u); setAction(''); }}
                      sx={{ fontSize: '0.7rem', color: C.teal, borderColor: C.teal,
                            borderRadius: 1, py: 0.3, px: 1, minWidth: 0,
                            '&:hover': { background: `${C.teal}18` } }}>
                      Actions
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} sx={{ ...TD, color: C.muted, textAlign: 'center', py: 4 }}>
                  No users found
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* User Detail Dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2 } }}>
        <DialogTitle sx={{ color: C.text, pb: 0 }}>
          @{selected?.user?.alias}
          <Typography sx={{ color: C.muted, fontSize: '0.78rem', mt: 0.3 }}>
            {selected?.user?.phone} · {selected?.user?.gender} · joined {selected?.user?.created_at ? new Date(selected.user.created_at).toLocaleDateString('en-IN') : '—'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {statusChip(selected.user.effectiveStatus, statusColor(selected.user.effectiveStatus))}
                {selected.user.needs_review && statusChip('Needs Review', C.amber)}
                {selected.user.warning_count > 0 && statusChip(`${selected.user.warning_count} warning(s)`, C.amber)}
                {selected.user.manual_override && statusChip(`Override: ${selected.user.manual_override}`, C.purple)}
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box><Typography sx={{ color: C.muted, fontSize: '0.72rem' }}>Game score</Typography>
                  <Typography sx={{ color: C.text, fontWeight: 700 }}>{selected.user.game_score_total || 0}</Typography></Box>
                <Box><Typography sx={{ color: C.muted, fontSize: '0.72rem' }}>Trophies</Typography>
                  <Typography sx={{ color: C.text, fontWeight: 700 }}>{selected.user.tournament_wins || 0}</Typography></Box>
                <Box><Typography sx={{ color: C.muted, fontSize: '0.72rem' }}>Invites sent</Typography>
                  <Typography sx={{ color: C.text, fontWeight: 700 }}>{selected.user.invites_sent || 0}</Typography></Box>
              </Box>
              {selected.reports?.length > 0 && (
                <Box>
                  <Typography sx={{ color: C.muted, fontSize: '0.75rem', mb: 0.5, fontWeight: 600 }}>RECENT REPORTS AGAINST THIS USER</Typography>
                  {selected.reports.map(r => (
                    <Box key={r.id} sx={{ background: C.surface2, borderRadius: 1, p: 1, mb: 0.5 }}>
                      <Typography sx={{ color: C.amber, fontSize: '0.78rem', fontWeight: 600 }}>{r.reason}</Typography>
                      <Typography sx={{ color: C.muted, fontSize: '0.72rem' }}>{r.venue_name} · {new Date(r.created_at).toLocaleDateString('en-IN')}</Typography>
                      {r.notes && <Typography sx={{ color: C.text, fontSize: '0.75rem', mt: 0.3 }}>{r.notes}</Typography>}
                    </Box>
                  ))}
                </Box>
              )}
              <Divider sx={{ borderColor: C.border }} />
              <Box>
                <Button size="small" variant="outlined"
                  onClick={() => { setSelected(null); setActionUser(selected.user); setAction(''); }}
                  sx={{ color: C.teal, borderColor: C.teal, fontSize: '0.78rem',
                        '&:hover': { background: `${C.teal}18` } }}>
                  Take Action on This User
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelected(null)} sx={{ color: C.muted }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={!!actionUser} onClose={() => { setActionUser(null); setAction(''); }} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2 } }}>
        <DialogTitle sx={{ color: C.text }}>Action on @{actionUser?.alias}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel sx={{ color: C.muted }}>Choose action</InputLabel>
            <Select value={action} label="Choose action" onChange={e => setAction(e.target.value)}
              sx={{ color: C.text, '.MuiOutlinedInput-notchedOutline': { borderColor: C.border },
                    '.MuiSvgIcon-root': { color: C.muted } }}>
              {ACTION_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {action === 'suspend' && (
            <TextField type="number" label="Days" value={days}
              onChange={e => setDays(parseInt(e.target.value) || 1)}
              size="small" fullWidth sx={{ mt: 2,
                '& .MuiInputBase-input': { color: C.text },
                '& .MuiInputLabel-root': { color: C.muted },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }} />
          )}
          {action === 'ban' && (
            <Alert severity="warning" sx={{ mt: 2, fontSize: '0.78rem' }}>
              This permanently bans the user. They will be force-blocked and cannot log in.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setActionUser(null); setAction(''); }} sx={{ color: C.muted }}>Cancel</Button>
          <Button onClick={doAction} disabled={!action || working} variant="contained"
            sx={{ background: action === 'ban' ? C.red : C.teal, color: '#fff',
                  '&:hover': { background: action === 'ban' ? '#cc1030' : '#00a08a' } }}>
            {working ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: VENUES
// ─────────────────────────────────────────────────────────────────────────────
function VenuesTab() {
  const [venues, setVenues]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [adminDialog, setAdminDialog] = useState(null);  // venue to assign admin to
  const [adminPhone, setAdminPhone] = useState('');
  const [adminRole, setAdminRole]   = useState('owner');
  const [working, setWorking]       = useState(false);
  const [toast, setToast]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/venues`, { headers: authHeaders() });
      const data = await res.json();
      setVenues(data.venues || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleQR(venueId, current) {
    try {
      await fetch(`${API}/api/admin/venues/${venueId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ qr_entry_enabled: current ? 0 : 1 }),
      });
      load();
    } catch (_) {}
  }

  async function assignAdmin() {
    if (!adminPhone || !adminDialog) return;
    setWorking(true);
    try {
      const res = await fetch(`${API}/api/admin/venue-admins`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ venue_id: adminDialog.id, phone: adminPhone, role: adminRole }),
      });
      const data = await res.json();
      if (data.ok) {
        setToast(`Admin assigned to ${adminDialog.name}`);
        setAdminDialog(null);
        setAdminPhone('');
        load();
      }
    } catch (_) {}
    setWorking(false);
  }

  return (
    <Box>
      {loading ? <CircularProgress size={24} sx={{ color: C.teal }} /> : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Venue','City / Area','QR','Mode','Admin Phone','Sub Status','Actions'].map(h => (
                  <TableCell key={h} sx={TH}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {venues.map(v => (
                <TableRow key={v.id} sx={{ '&:hover': { background: C.surface2 } }}>
                  <TableCell sx={TD}>
                    <Typography sx={{ color: C.text, fontSize: '0.82rem', fontWeight: 600 }}>{v.name}</Typography>
                    <Typography sx={{ color: C.muted, fontSize: '0.7rem' }}>
                      {v.active_checkins_now} now · {v.checkins_today} today
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...TD, color: C.muted }}>{v.city}{v.area ? ` · ${v.area}` : ''}</TableCell>
                  <TableCell sx={TD}>
                    <Chip label={v.qr_entry_enabled ? 'ON' : 'OFF'} size="small" clickable
                      onClick={() => toggleQR(v.id, v.qr_entry_enabled)}
                      sx={{
                        background: v.qr_entry_enabled ? `${C.green}22` : `${C.muted}22`,
                        color: v.qr_entry_enabled ? C.green : C.muted,
                        fontSize: '0.68rem', fontWeight: 700, height: 20,
                      }} />
                  </TableCell>
                  <TableCell sx={{ ...TD, color: C.muted }}>{v.checkin_mode || 'auto'}</TableCell>
                  <TableCell sx={{ ...TD, color: C.muted }}>{v.admin_phone || '—'}</TableCell>
                  <TableCell sx={TD}>{subStatusChip(v.valid_until)}</TableCell>
                  <TableCell sx={TD}>
                    <Button size="small" onClick={() => { setAdminDialog(v); setAdminPhone(v.admin_phone || ''); }}
                      sx={{ fontSize: '0.7rem', color: C.teal, borderColor: C.teal,
                            border: `1px solid ${C.teal}`, borderRadius: 1, py: 0.3, px: 1, minWidth: 0,
                            '&:hover': { background: `${C.teal}18` } }}>
                      Assign Admin
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Assign Admin Dialog */}
      <Dialog open={!!adminDialog} onClose={() => setAdminDialog(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2 } }}>
        <DialogTitle sx={{ color: C.text }}>Assign Admin — {adminDialog?.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Phone number" value={adminPhone} onChange={e => setAdminPhone(e.target.value)}
            size="small" fullWidth
            sx={{ '& .MuiInputBase-input': { color: C.text },
                  '& .MuiInputLabel-root': { color: C.muted },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }} />
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ color: C.muted }}>Role</InputLabel>
            <Select value={adminRole} label="Role" onChange={e => setAdminRole(e.target.value)}
              sx={{ color: C.text, '.MuiOutlinedInput-notchedOutline': { borderColor: C.border },
                    '.MuiSvgIcon-root': { color: C.muted } }}>
              <MenuItem value="owner">Owner</MenuItem>
              <MenuItem value="staff">Staff</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdminDialog(null)} sx={{ color: C.muted }}>Cancel</Button>
          <Button onClick={assignAdmin} disabled={!adminPhone || working} variant="contained"
            sx={{ background: C.teal, color: '#fff', '&:hover': { background: '#00a08a' } }}>
            {working ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: REPORTS
// ─────────────────────────────────────────────────────────────────────────────
function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/reports`, { headers: authHeaders() });
      const data = await res.json();
      setReports(data.reports || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function markReviewed(userId) {
    try {
      await fetch(`${API}/api/admin/reports/${userId}/reviewed`, {
        method: 'PATCH', headers: authHeaders(),
      });
      setToast('Marked as reviewed');
      load();
    } catch (_) {}
  }

  const REASON_LABELS = {
    not_at_venue:             'Not at venue',
    inappropriate_alias:      'Inappropriate alias',
    inappropriate_behaviour:  'Inappropriate behaviour',
    other:                    'Other',
  };

  return (
    <Box>
      {loading ? <CircularProgress size={24} sx={{ color: C.teal }} /> : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Reported User','Venue','Reason','Notes','Filed At','Status','Action'].map(h => (
                  <TableCell key={h} sx={TH}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map(r => (
                <TableRow key={r.id}
                  sx={{ background: r.needs_review ? `${C.amber}10` : 'transparent',
                        '&:hover': { background: C.surface2 } }}>
                  <TableCell sx={TD}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      {r.needs_review ? '🔴' : '✅'}
                      @{r.reported_alias}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...TD, color: C.muted }}>{r.venue_name}</TableCell>
                  <TableCell sx={TD}>
                    <Chip label={REASON_LABELS[r.reason] || r.reason} size="small"
                      sx={{ background: `${C.amber}22`, color: C.amber, fontSize: '0.68rem', height: 20 }} />
                  </TableCell>
                  <TableCell sx={{ ...TD, color: C.muted, maxWidth: 200 }}>
                    <Typography noWrap sx={{ fontSize: '0.78rem', color: C.muted }}>{r.notes || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...TD, color: C.muted }}>
                    {new Date(r.created_at).toLocaleDateString('en-IN')}
                  </TableCell>
                  <TableCell sx={TD}>
                    {r.is_banned ? statusChip('BANNED', C.red)
                      : r.is_suspended ? statusChip('SUSPENDED', C.amber)
                      : statusChip('Active', C.green)}
                  </TableCell>
                  <TableCell sx={TD}>
                    {r.needs_review && (
                      <Button size="small" onClick={() => markReviewed(r.user_id)}
                        sx={{ fontSize: '0.7rem', color: C.green, borderColor: C.green,
                              border: `1px solid ${C.green}`, borderRadius: 1, py: 0.3, px: 1, minWidth: 0,
                              '&:hover': { background: `${C.green}18` } }}>
                        Mark Reviewed
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {reports.length === 0 && (
                <TableRow><TableCell colSpan={7} sx={{ ...TD, color: C.muted, textAlign: 'center', py: 4 }}>
                  No reports yet
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: CONFIG
// ─────────────────────────────────────────────────────────────────────────────
function ConfigTab() {
  const [config, setConfig]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits]     = useState({});
  const [saving, setSaving]   = useState({});
  const [toast, setToast]     = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/config`, { headers: authHeaders() });
        const data = await res.json();
        setConfig(data.config || []);
        const initial = {};
        (data.config || []).forEach(c => { initial[c.config_key] = c.config_value; });
        setEdits(initial);
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  async function save(key) {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await fetch(`${API}/api/admin/config`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ key_name: key, value: edits[key] }),
      });
      setToast(`Saved: ${key}`);
    } catch (_) {}
    setSaving(s => ({ ...s, [key]: false }));
  }

  return (
    <Box>
      <Typography sx={{ color: C.muted, fontSize: '0.8rem', mb: 2 }}>
        Changes take effect immediately — no server restart needed.
      </Typography>
      {loading ? <CircularProgress size={24} sx={{ color: C.teal }} /> : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 560 }}>
          {config.map(c => (
            <Paper key={c.config_key} sx={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5 }}>
              <Typography sx={{ color: C.muted, fontSize: '0.72rem', mb: 0.5, fontWeight: 600 }}>
                {c.config_key}
              </Typography>
              {c.description && (
                <Typography sx={{ color: C.dim, fontSize: '0.7rem', mb: 0.8 }}>{c.description}</Typography>
              )}
              {c.data_type === 'boolean' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Switch
                    checked={edits[c.config_key] === 'true'}
                    onChange={e => {
                      const val = e.target.checked ? 'true' : 'false';
                      setEdits(prev => ({ ...prev, [c.config_key]: val }));
                      setSaving(s => ({ ...s, [c.config_key]: true }));
                      fetch(`${API}/api/admin/config`, {
                        method: 'PUT',
                        headers: authHeaders(),
                        body: JSON.stringify({ key_name: c.config_key, value: val }),
                      }).then(() => {
                        setToast(`Saved: ${c.config_key} = ${val}`);
                        setSaving(s => ({ ...s, [c.config_key]: false }));
                      });
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: C.teal },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: C.teal },
                    }}
                  />
                  <Typography sx={{ color: edits[c.config_key] === 'true' ? C.green : C.red, fontSize: '0.85rem', fontWeight: 600 }}>
                    {edits[c.config_key] === 'true' ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField size="small" value={edits[c.config_key] ?? ''}
                    type={c.data_type === 'integer' ? 'number' : 'text'}
                    placeholder={c.data_type === 'integer' ? 'empty = auto' : ''}
                    onChange={e => setEdits(prev => ({ ...prev, [c.config_key]: e.target.value }))}
                    sx={{ flex: 1,
                          '& .MuiInputBase-input': { color: C.text, fontSize: '0.85rem' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }} />
                  <Button size="small" onClick={() => save(c.config_key)}
                    disabled={saving[c.config_key]}
                    sx={{ background: C.teal, color: '#fff', fontSize: '0.72rem', minWidth: 60,
                          '&:hover': { background: '#00a08a' }, '&.Mui-disabled': { background: C.dim } }}>
                    {saving[c.config_key] ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
                  </Button>
                </Box>
              )}
            </Paper>
          ))}
          {config.length === 0 && (
            <Typography sx={{ color: C.muted }}>No config keys found in app_config table.</Typography>
          )}
        </Box>
      )}
      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// IMPORT TAB
// ─────────────────────────────────────────────────────────────────────────────
function CsvImporter({ title, description, formatExample, previewColumns, endpoint, bodyKey, successMsg }) {
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  function parseCsv(text) {
    const lines = text.trim().split('\n').filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
      return obj;
    });
  }

  function handlePreview() {
    setError(''); setResult(null);
    const rows = parseCsv(csvText);
    if (rows.length === 0) { setError('No valid rows found. Check format.'); return; }
    setPreview(rows);
  }

  async function handleImport() {
    if (!preview || preview.length === 0) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ [bodyKey]: preview }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Import failed'); setLoading(false); return; }
      setResult(data); setPreview(null); setCsvText('');
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography sx={{ color: C.muted, fontSize: '0.8rem', mb: 2 }}>{description}</Typography>
      <Typography sx={{ color: C.muted, fontSize: '0.72rem', mb: 0.5, fontWeight: 600 }}>EXPECTED FORMAT</Typography>
      <Paper sx={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, mb: 2 }}>
        <Typography sx={{ color: C.dim, fontSize: '0.72rem', fontFamily: 'monospace', whiteSpace: 'pre' }}>
          {formatExample}
        </Typography>
      </Paper>
      <TextField
        multiline rows={8} fullWidth placeholder="Paste CSV here..."
        value={csvText}
        onChange={e => { setCsvText(e.target.value); setPreview(null); setResult(null); setError(''); }}
        sx={{
          mb: 1.5,
          '& .MuiInputBase-input': { color: C.text, fontSize: '0.8rem', fontFamily: 'monospace' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.teal },
        }}
      />
      {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>{error}</Alert>}
      {!preview && !result && (
        <Button onClick={handlePreview} disabled={!csvText.trim()}
          sx={{ background: C.teal, color: '#fff', fontWeight: 700, borderRadius: 1.5,
                '&:hover': { background: '#00a08a' }, '&.Mui-disabled': { background: C.dim } }}>
          Preview
        </Button>
      )}
      {preview && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {preview.length} row{preview.length !== 1 ? 's' : ''} ready to import. Existing IDs will be skipped.
          </Alert>
          <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {previewColumns.map(h => (
                    <TableCell key={h} sx={{ color: C.muted, fontSize: '0.7rem', borderColor: C.border }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.slice(0, 5).map((r, i) => (
                  <TableRow key={i}>
                    {previewColumns.map(h => (
                      <TableCell key={h} sx={{ color: C.text, fontSize: '0.75rem', borderColor: C.border }}>{r[h] || '\u2014'}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {preview.length > 5 && (
              <Typography sx={{ color: C.dim, fontSize: '0.72rem', mt: 0.5 }}>
                ...and {preview.length - 5} more
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button onClick={handleImport} disabled={loading}
              sx={{ background: C.green, color: '#000', fontWeight: 700, borderRadius: 1.5,
                    '&:hover': { background: '#00c080' }, '&.Mui-disabled': { background: C.dim } }}>
              {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm Import'}
            </Button>
            <Button onClick={() => setPreview(null)}
              sx={{ color: C.muted, borderColor: C.border, border: '1px solid', borderRadius: 1.5 }}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}
      {result && (
        <Alert severity="success" sx={{ mt: 2, fontSize: '0.85rem' }}>
          {successMsg(result)}
        </Alert>
      )}
    </Box>
  );
}

function ImportTab() {
  const [subTab, setSubTab] = useState(0);
  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)}
        sx={{ mb: 3, borderBottom: `1px solid ${C.border}`,
              '& .MuiTab-root': { color: C.muted, fontSize: '0.8rem', textTransform: 'none', minWidth: 0, mr: 2 },
              '& .Mui-selected': { color: C.teal },
              '& .MuiTabs-indicator': { background: C.teal } }}>
        <Tab label="🏢 Venues" />
        <Tab label="🏆 Tournament Questions" />
        <Tab label="📝 Trivia Questions" />
      </Tabs>
      {subTab === 0 && (
        <CsvImporter
          description="Required columns: id, name. Optional: city, area, latitude, longitude."
          formatExample={"id,city,name,area,latitude,longitude\nvenue-001,Mumbai,The Garage,Bandra,19.05490600,72.98880000"}
          previewColumns={['id','name','city','area','latitude','longitude']}
          endpoint="/api/admin/import/venues"
          bodyKey="venues"
          successMsg={r => `\u2713 Done \u2014 ${r.inserted} inserted, ${r.skipped} skipped.`}
        />
      )}
      {subTab === 1 && (
        <CsvImporter
          description="Required: id, phase, category, question, option_a-d, correct. Phase: ROUND_1 / ROUND_2 / SEMI / FINAL / SUDDEN_DEATH."
          formatExample={"id,phase,category,question,option_a,option_b,option_c,option_d,correct\ntq_sci_011,ROUND_1,science,What is H2O?,Water,Salt,Oil,Acid,A"}
          previewColumns={['id','phase','category','question','option_a','option_b','option_c','option_d','correct']}
          endpoint="/api/admin/import/tournament-questions"
          bodyKey="questions"
          successMsg={r => `✓ Done — ${r.inserted} inserted, ${r.skipped} skipped.`}
        />
      )}
      {subTab === 2 && (
        <CsvImporter
          description="Required: id, category, question, option_a, option_b, option_c, option_d, correct. Categories: general_knowledge, pop_culture, food_and_drink, sports, fun_and_random."
          formatExample={"id,category,question,option_a,option_b,option_c,option_d,correct\ngk_001,general_knowledge,What is the capital of Japan?,Beijing,Seoul,Tokyo,Bangkok,C"}
          previewColumns={['id','category','question','option_a','option_b','option_c','option_d','correct']}
          endpoint="/api/admin/import/trivia-questions"
          bodyKey="questions"
          successMsg={r => `✓ Done — ${r.inserted} inserted, ${r.skipped} skipped.`}
        />
      )}
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MAIN — SuperAdmin
// ─────────────────────────────────────────────────────────────────────────────
export default function SuperAdmin() {
  const [tab, setTab]       = useState(0);
  const [stats, setStats]   = useState(null);
  const [authOk, setAuthOk] = useState(null);  // null=loading, true=ok, false=denied

  // Verify the token is an admin token
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('binko_token');
      if (!token) { setAuthOk(false); return; }
      try {
        const res = await fetch(`${API}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401 || res.status === 403) { setAuthOk(false); return; }
        const data = await res.json();
        setStats(data);
        setAuthOk(true);
      } catch (_) {
        setAuthOk(false);
      }
    })();
  }, []);

  // ── Not authorised ──────────────────────────────────────────────────────────
  if (authOk === null) {
    return (
      <Box sx={{ minHeight: '100vh', background: C.bg, display: 'flex',
                 alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={32} sx={{ color: C.teal }} />
      </Box>
    );
  }

  if (authOk === false) {
    return (
      <Box sx={{ minHeight: '100vh', background: C.bg, display: 'flex',
                 flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <Typography sx={{ color: C.red, fontSize: '1.1rem', fontWeight: 700 }}>🚫 Access Denied</Typography>
        <Typography sx={{ color: C.muted, fontSize: '0.9rem' }}>
          Your account is not authorised as a Binko super admin.
        </Typography>
        <Typography sx={{ color: C.dim, fontSize: '0.8rem' }}>
          Log into Binko first, then come back to this URL.
        </Typography>
        <Button onClick={() => window.location.href = '/'} sx={{ color: C.teal, mt: 1 }}>
          Go to app
        </Button>
      </Box>
    );
  }

  const TABS = ['📊 Overview', '👥 Users', '🏢 Venues', '🚩 Reports', '⚙️ Config', '📥 Import'];

  return (
    <Box sx={{ minHeight: '100vh', background: C.bg, color: C.text,
               fontFamily: 'Plus Jakarta Sans, Arial, sans-serif' }}>

      {/* Header */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 100 }}>
      <Box sx={{ background: C.surface, borderBottom: `1px solid ${C.border}`,
                 px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ color: C.teal, fontWeight: 800, fontSize: '1.1rem', letterSpacing: 2 }}>
            BINKO
          </Typography>
          <Chip label="SUPER ADMIN" size="small"
            sx={{ background: `${C.purple}22`, color: C.purple, fontWeight: 700, fontSize: '0.68rem' }} />
        </Box>
        <Button onClick={() => window.location.href = '/home'}
          sx={{ color: C.muted, fontSize: '0.78rem' }}>
          ← Back to app
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ background: C.surface, borderBottom: `1px solid ${C.border}`, px: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          TabIndicatorProps={{ style: { background: C.teal } }}
          sx={{ '& .MuiTab-root': { color: C.muted, fontSize: '0.82rem', minWidth: 0, px: 2 },
               '& .Mui-selected': { color: C.teal } }}>
          {TABS.map(t => <Tab key={t} label={t} />)}
        </Tabs>
      </Box>

      </Box>
      {/* Content */}
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>

        {/* Overview tab */}
        {tab === 0 && (
          <Box>
            <Typography sx={{ color: C.muted, fontSize: '0.8rem', mb: 2 }}>
              Live stats — refreshed on load
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
              <StatCard label="Total Users"      value={stats?.total_users}   color={C.teal} />
              <StatCard label="Active Today"     value={stats?.active_today}  color={C.green} />
              <StatCard label="Needs Review"     value={stats?.needs_review}  color={stats?.needs_review > 0 ? C.amber : C.muted} />
              <StatCard label="Total Venues"     value={stats?.total_venues}  color={C.purple} />
              <StatCard label="Venues Active Today" value={stats?.active_venues} color={C.teal} />
            </Box>
            <Divider sx={{ borderColor: C.border, mb: 3 }} />
            <Typography sx={{ color: C.muted, fontSize: '0.82rem', mb: 1.5, fontWeight: 600 }}>QUICK ACTIONS</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {[
                { label: '👥 Manage Users', tab: 1 },
                { label: '🏢 Manage Venues', tab: 2 },
                { label: '🚩 Review Reports', tab: 3 },
                { label: '⚙️ Edit Config', tab: 4 },
              ].map(q => (
                <Button key={q.tab} onClick={() => setTab(q.tab)} variant="outlined"
                  sx={{ color: C.text, borderColor: C.border, borderRadius: 1.5, fontSize: '0.82rem',
                        '&:hover': { background: C.surface2, borderColor: C.teal } }}>
                  {q.label}
                  {q.tab === 3 && stats?.needs_review > 0 && (
                    <Chip label={stats.needs_review} size="small"
                      sx={{ ml: 1, background: C.amber, color: '#000', fontSize: '0.65rem',
                            height: 18, fontWeight: 700 }} />
                  )}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {tab === 1 && <UsersTab />}
        {tab === 2 && <VenuesTab />}
        {tab === 3 && <ReportsTab />}
        {tab === 4 && <ConfigTab />}
        {tab === 5 && <ImportTab />}
      </Box>
    </Box>
  );
}
